"use client";

import { cn } from "@/lib/utils";
import { MapPinIcon } from "lucide-react";
import { useEffect, type RefObject } from "react";

export type AreaHoverAction = { type: "enter" | "leave"; id: number };

export function MapAreaMarker({
  markerElementRef,
  name,
  count,
  areaId,
  isHighlighted,
  isActive,
  onHover,
  onClick,
}: {
  markerElementRef: RefObject<HTMLDivElement | null>;
  name: string;
  count: number;
  areaId: number;
  isHighlighted: boolean;
  isActive: boolean;
  onHover: (action: AreaHoverAction) => void;
  onClick: () => void;
}) {
  useEffect(() => {
    if (!isActive) return;
    onHover({ type: "enter", id: areaId });
    return () => onHover({ type: "leave", id: areaId });
  }, [isActive, areaId, onHover]);

  const label =
    count === 1
      ? `1 listing in ${name} without an exact address`
      : `${count.toLocaleString()} listings in ${name} without an exact address`;

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Map markers are pointer-only, same as price pins
    <div
      ref={markerElementRef}
      onClick={onClick}
      onPointerEnter={() => onHover({ type: "enter", id: areaId })}
      onPointerLeave={() => {
        if (!isActive) onHover({ type: "leave", id: areaId });
      }}
      title={label}
      className={cn(
        "inline-flex w-max max-w-[16rem] h-11 cursor-pointer items-center gap-1 rounded-full border border-dashed px-3 text-xs font-medium shadow-md sm:h-8",
        isHighlighted
          ? "border-emerald-700 bg-emerald-600 text-white"
          : "border-emerald-700 bg-card text-foreground",
      )}
    >
      <MapPinIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
      <span className="max-w-[10rem] truncate">{name}</span>
      <span aria-hidden>·</span>
      <span className="font-data">{count.toLocaleString()}</span>
    </div>
  );
}
